// 📄 pages/Admin/sections/ApiCatalogSection.tsx
// Catálogo de APIs Estándar: plantillas globales (sin credenciales) que
// sirven de referencia/preconfiguración para lo que un consultor arma
// después por cliente en Configuración > APIs (ver ConfiguracionApis.tsx)
// y Configuración > SUNAT (ver ConfiguracionSunat.tsx). Tres bloques:
// A) URL Base estándar de SUNAT (urls/urlsunat, mismo doc que ya lee
// sunatService.js como fallback global), B) plantillas Ariba y C)
// plantillas S/4HANA (catalogoApis/{id}, ver catalogoApisService.ts).
import { useEffect, useState } from "react";
import { RefreshCw, Save, Plus, X, Info, Globe } from "lucide-react";
import {
  ApiTemplate,
  ApiTemplateData,
  getApiTemplates,
  saveApiTemplate,
  deleteApiTemplate,
  getSunatUrlEstandar,
  saveSunatUrlEstandar
} from "../../../services/catalogoApisService.ts";
import { ApiTipoId } from "../../../services/clienteConexionService.ts";
import { TIPOS_API_LISTA } from "../../../components/Configuracion/apiTiposConfig.ts";
import ApiTemplateCard from "../components/ApiTemplateCard.tsx";
import NuevaPlantillaModal from "../components/NuevaPlantillaModal.tsx";

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export default function ApiCatalogSection() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // A. SUNAT
  const [sunatUrl, setSunatUrl] = useState("");
  const [guardandoSunat, setGuardandoSunat] = useState(false);

  // B/C. Plantillas Ariba / S/4HANA
  const [templates, setTemplates] = useState<ApiTemplate[]>([]);
  const [expandidas, setExpandidas] = useState<Record<string, boolean>>({});
  const [guardandoId, setGuardandoId] = useState<string | null>(null);
  const [mostrarSelectorTipo, setMostrarSelectorTipo] = useState(false);
  // Tipo elegido en el selector mientras el modal de alta está abierto —
  // null = modal cerrado. El draft de la plantilla nueva vive DENTRO del
  // modal (ver NuevaPlantillaModal.tsx); acá nunca se toca `templates`
  // hasta que el guardado sea exitoso, así cancelar no deja nada a medias.
  const [tipoNuevaPlantilla, setTipoNuevaPlantilla] = useState<ApiTipoId | null>(null);

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const [url, temps] = await Promise.all([getSunatUrlEstandar(), getApiTemplates()]);
      setSunatUrl(url);
      setTemplates(temps);
    } catch (err) {
      console.error("Error cargando el catálogo de APIs:", err);
      setError("No se pudo cargar el catálogo de APIs estándar.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const showMessage = (setter: (v: string | null) => void, msg: string) => {
    setter(msg);
    setTimeout(() => setter(null), 4000);
  };

  const handleGuardarSunat = async () => {
    setGuardandoSunat(true);
    setError(null);
    try {
      await saveSunatUrlEstandar(sunatUrl);
      showMessage(setSuccess, "URL de SUNAT guardada correctamente.");
    } catch (err: any) {
      showMessage(setError, err.message || "Error al guardar la URL de SUNAT.");
    } finally {
      setGuardandoSunat(false);
    }
  };

  const handleElegirTipoNuevaPlantilla = (tipo: ApiTipoId) => {
    setTipoNuevaPlantilla(tipo);
    setMostrarSelectorTipo(false);
  };

  // Guarda de una plantilla recién creada en el modal: mismo
  // saveApiTemplate(...) que usa handleGuardarTemplate para ediciones, con
  // esNueva=true. Recién acá se agrega a `templates` — si falla, el modal
  // se queda abierto (ver NuevaPlantillaModal.tsx) y este catch nunca
  // corre, así que la lista tampoco cambia.
  const handleGuardarNuevaPlantilla = async (data: ApiTemplateData) => {
    const id = generarId();
    await saveApiTemplate(id, data, true);
    setTemplates((prev) => [...prev, { id, ...data }]);
    setTipoNuevaPlantilla(null);
    showMessage(setSuccess, "Plantilla guardada correctamente.");
  };

  const handleActualizarTemplate = (id: string, data: ApiTemplateData) => {
    setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, ...data } : t)));
  };

  const toggleExpandida = (id: string) => {
    setExpandidas((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleGuardarTemplate = async (template: ApiTemplate) => {
    setGuardandoId(template.id);
    setError(null);
    try {
      const { id, createdAt, updatedAt, ...data } = template;
      await saveApiTemplate(id, data, false);
      showMessage(setSuccess, "Plantilla guardada correctamente.");
    } catch (err: any) {
      showMessage(setError, err.message || "Error al guardar la plantilla.");
    } finally {
      setGuardandoId(null);
    }
  };

  const handleEliminarTemplate = async (template: ApiTemplate) => {
    if (!window.confirm(`¿Eliminar la plantilla "${template.nombre || "sin nombre"}"? Esta acción no se puede deshacer.`)) return;

    try {
      await deleteApiTemplate(template.id);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
    } catch (err: any) {
      showMessage(setError, err.message || "Error al eliminar la plantilla.");
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
        <p className="text-sm">Cargando catálogo de APIs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-light text-white">Catálogo de APIs Estándar</h1>
        <p className="text-sm text-gray-400 mt-1">Plantillas globales de URLs y endpoints (SUNAT, SAP Ariba, SAP S/4HANA Cloud) usadas como referencia al configurar un cliente. Nunca incluyen credenciales.</p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs">{success}</div>
      )}

      {/* A. SUNAT / Servicios Externos */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <Globe className="w-4 h-4 text-indigo-400" /> SUNAT / Servicios Externos
        </h2>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
          <div>
            <label className={labelClass}>URL Base estándar del servicio SUNAT</label>
            <input
              type="text"
              className={inputClass}
              value={sunatUrl}
              onChange={(e) => setSunatUrl(e.target.value)}
              placeholder="https://e-factura.sunat.gob.pe/..."
            />
            <p className="text-[11px] text-gray-500 mt-1">Se usa como valor por defecto para cualquier cliente que no configure su propia URL en Configuración &gt; SUNAT.</p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleGuardarSunat}
              disabled={guardandoSunat}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {guardandoSunat ? "Guardando..." : "Guardar URL"}
            </button>
          </div>
        </div>
      </section>

      {/* B/C. Plantillas de APIs */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-300">Plantillas de APIs (Ariba / S/4HANA)</h2>
          <button
            type="button"
            onClick={() => setMostrarSelectorTipo((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            {mostrarSelectorTipo ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            {mostrarSelectorTipo ? "Cancelar" : "Agregar plantilla"}
          </button>
        </div>

        <div className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 p-3 rounded-lg text-xs flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Estas plantillas nunca guardan API Key, OAuth Secret, usuario ni contraseña — esas credenciales se configuran siempre por cliente.</span>
        </div>

        {mostrarSelectorTipo && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
            <p className="text-xs font-semibold text-gray-300">¿Qué tipo de plantilla vas a crear?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TIPOS_API_LISTA.map((tipoMeta) => (
                <button
                  key={tipoMeta.id}
                  type="button"
                  onClick={() => handleElegirTipoNuevaPlantilla(tipoMeta.id)}
                  className="text-left bg-black/20 hover:bg-black/30 border border-white/10 hover:border-indigo-500/50 rounded-xl p-4 transition-all"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xl leading-none">{tipoMeta.icon}</span>
                    <span className="text-sm font-semibold text-white">{tipoMeta.label}</span>
                  </div>
                  <p className="text-xs text-gray-400">{tipoMeta.descripcion}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {templates.length === 0 ? (
          <p className="text-xs text-gray-500 bg-white/5 border border-white/10 rounded-2xl p-5">
            Todavía no hay plantillas en el catálogo.
          </p>
        ) : (
          <div className="space-y-8">
            {TIPOS_API_LISTA.map((tipoMeta) => {
              const templatesDelTipo = templates.filter((t) => (t.tipo || "ariba") === tipoMeta.id);
              if (templatesDelTipo.length === 0) return null;

              return (
                <div key={tipoMeta.id} className="space-y-3">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <span className="text-sm leading-none">{tipoMeta.icon}</span> {tipoMeta.label}
                  </h3>
                  <div className="space-y-5">
                    {templatesDelTipo.map((template) => (
                      <ApiTemplateCard
                        key={template.id}
                        template={template}
                        expandida={!!expandidas[template.id]}
                        guardando={guardandoId === template.id}
                        onToggleExpandida={() => toggleExpandida(template.id)}
                        onChange={(data) => handleActualizarTemplate(template.id, data)}
                        onGuardar={() => handleGuardarTemplate(template)}
                        onEliminar={() => handleEliminarTemplate(template)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {tipoNuevaPlantilla && (
        <NuevaPlantillaModal
          tipo={tipoNuevaPlantilla}
          onGuardar={handleGuardarNuevaPlantilla}
          onCancelar={() => setTipoNuevaPlantilla(null)}
        />
      )}
    </div>
  );
}
